import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { clsx } from 'clsx';
import { Lock, BadgeCheck, Medal } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { AGENT_MEDALS, earnedMedals, CBR_AGENCY_MEDAL_ID } from '../../lib/medals';

/**
 * Dashboard top-bar medals: the 5 agent medals (earned in color, pending
 * grayed with a how-to tooltip) + the agency's CBR medal, which stays hidden
 * until held. Clicking opens a popover with every medal explained. On mount it
 * calls refresh_user_badges() so newly-qualified medals appear without a
 * manual action — the RPC re-verifies server-side and is idempotent.
 */
export const MedalsBar: React.FC<{ theme?: 'light' | 'dark' }> = ({ theme = 'dark' }) => {
    const isLight = theme !== 'dark';
    const { profile, agency } = useAuth();
    const [badges, setBadges] = useState<Record<string, string>>((profile?.badges as Record<string, string>) || {});
    const [toolsAt, setToolsAt] = useState<string | null>(profile?.tools_badge_earned_at || null);
    const [open, setOpen] = useState(false);
    // Popover position — portaled to <body> so page banners (own stacking
    // contexts above the header) can never paint over it.
    const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
    const rootRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!supabase || !profile?.id) return;
        let cancelled = false;
        supabase.rpc('refresh_user_badges').then(({ data }) => {
            if (cancelled || !data) return;
            const d = data as any;
            setBadges((d.badges || {}) as Record<string, string>);
            if (d.tools_badge_earned_at) setToolsAt(d.tools_badge_earned_at);
        });
        return () => { cancelled = true; };
    }, [profile?.id]);

    // Close the popover on outside click.
    useEffect(() => {
        if (!open) return;
        const onDown = (e: MouseEvent) => {
            const t = e.target as HTMLElement;
            // The popover is portaled to <body>, so contains() on the trigger
            // wrapper misses it — check both before closing.
            if (rootRef.current?.contains(t) || t.closest?.('[data-medals-popover]')) return;
            setOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [open]);

    if (!profile || profile.role === 'super_admin') return null;

    const earned = earnedMedals({ tools_badge_earned_at: toolsAt, badges });
    const earnedCount = Object.values(earned).filter(Boolean).length;
    const hasCbr = Array.isArray(agency?.medals) && (agency!.medals as number[]).map(Number).includes(CBR_AGENCY_MEDAL_ID);

    return (
        <div ref={rootRef} className="relative hidden sm:block">
            <button
                onClick={e => {
                    const r = (e.currentTarget as HTMLButtonElement).getBoundingClientRect();
                    setPos({ top: r.bottom + 8, right: Math.max(8, window.innerWidth - r.right) });
                    setOpen(o => !o);
                }}
                aria-label="Ver mis medallas"
                className={clsx(
                    'flex items-center gap-1.5 p-2 pr-3 rounded-xl border transition-all',
                    isLight ? 'bg-slate-100 border-slate-200 hover:bg-slate-200/70' : 'bg-white/5 border-white/5 hover:bg-white/10'
                )}
            >
                {/* CBR — agency medal, only visible once held */}
                {hasCbr && (
                    <span className="group/medal relative w-6 h-6 rounded-full bg-sky-500/15 text-sky-500 flex items-center justify-center">
                        <BadgeCheck size={14} />
                        <MedalTip isLight={isLight} text="CBR Guatemala — agencia asociada a la Cámara de Corredores de Bienes Raíces" />
                    </span>
                )}
                {AGENT_MEDALS.map(m => {
                    const isEarned = !!earned[m.key];
                    const Icon = m.icon;
                    return (
                        <span
                            key={m.key}
                            className={clsx(
                                'group/medal relative w-6 h-6 rounded-full flex items-center justify-center',
                                isEarned
                                    ? 'bg-emerald-500/15 text-emerald-500'
                                    : isLight ? 'bg-slate-200/70 text-slate-400' : 'bg-white/5 text-slate-600'
                            )}
                        >
                            <Icon size={13} />
                            <MedalTip isLight={isLight} text={isEarned ? `${m.name} — ¡obtenida!` : `${m.name}: ${m.how}`} />
                        </span>
                    );
                })}
                <span className={clsx('text-[10px] font-black ml-0.5', isLight ? 'text-slate-500' : 'text-slate-400')}>
                    {earnedCount}/{AGENT_MEDALS.length}
                </span>
            </button>

            {open && pos && createPortal(
                <div data-medals-popover style={{ position: 'fixed', top: pos.top, right: pos.right }} className={clsx(
                    'w-[340px] rounded-2xl border shadow-2xl p-4 z-[9999]',
                    isLight ? 'bg-white border-slate-200' : 'bg-slate-900 border-white/10'
                )}>
                    <p className={clsx('flex items-center gap-2 text-xs font-black uppercase tracking-widest mb-3', isLight ? 'text-slate-500' : 'text-slate-400')}>
                        <Medal size={14} className="text-amber-500" /> Mis medallas
                    </p>
                    <div className="flex flex-col gap-2.5">
                        {hasCbr && (
                            <div className="flex items-start gap-3">
                                <span className="w-8 h-8 rounded-xl bg-sky-500/15 text-sky-500 flex items-center justify-center shrink-0"><BadgeCheck size={16} /></span>
                                <div>
                                    <p className={clsx('text-sm font-bold', isLight ? 'text-slate-900' : 'text-white')}>CBR Guatemala</p>
                                    <p className={clsx('text-xs', isLight ? 'text-slate-500' : 'text-slate-400')}>Tu agencia es asociada de la Cámara de Corredores de Bienes Raíces.</p>
                                </div>
                            </div>
                        )}
                        {AGENT_MEDALS.map(m => {
                            const earnedAt = earned[m.key];
                            const Icon = m.icon;
                            return (
                                <div key={m.key} className={clsx('flex items-start gap-3', !earnedAt && 'opacity-70')}>
                                    <span className={clsx(
                                        'w-8 h-8 rounded-xl flex items-center justify-center shrink-0 relative',
                                        earnedAt ? 'bg-emerald-500/15 text-emerald-500' : isLight ? 'bg-slate-100 text-slate-400' : 'bg-white/5 text-slate-500'
                                    )}>
                                        <Icon size={16} />
                                        {!earnedAt && <Lock size={9} className="absolute -bottom-0.5 -right-0.5" />}
                                    </span>
                                    <div>
                                        <p className={clsx('text-sm font-bold', isLight ? 'text-slate-900' : 'text-white')}>
                                            {m.name}
                                            {earnedAt && <span className="ml-1.5 text-[9px] font-black uppercase text-emerald-500">Obtenida</span>}
                                        </p>
                                        <p className={clsx('text-xs', isLight ? 'text-slate-500' : 'text-slate-400')}>
                                            {earnedAt
                                                ? `Ganada el ${new Date(earnedAt).toLocaleDateString('es-GT')}`
                                                : m.how}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p className={clsx('text-[10px] mt-3 pt-3 border-t', isLight ? 'text-slate-400 border-slate-100' : 'text-slate-500 border-white/5')}>
                        Tus medallas son visibles para tus colegas — con más medallas apareces primero en la Red y en los portales.
                    </p>
                </div>,
                document.body
            )}
        </div>
    );
};


// Instant hover tooltip (native title is slow/flaky on nested spans in a button).
const MedalTip: React.FC<{ text: string; isLight: boolean }> = ({ text, isLight }) => (
    <span className={clsx(
        'pointer-events-none absolute top-full right-0 mt-2 w-56 rounded-xl border px-3 py-2 text-[11px] font-semibold leading-snug text-left normal-case tracking-normal shadow-xl z-[9999]',
        'opacity-0 group-hover/medal:opacity-100 transition-opacity duration-100',
        isLight ? 'bg-white border-slate-200 text-slate-700' : 'bg-slate-900 border-white/10 text-slate-200'
    )}>
        {text}
    </span>
);
